'use client';

// Презентационные блоки ответа генератора: аватар бота, отрисовка схемы,
// резюме «что понадобится» и подписи узлов. Логика диалога — в Generator.tsx.
import styles from './generator.module.css';
import { NodeIcon } from './icons';
import { type GeneratorDict, type SchemaNode } from './schema';

/** Логотип сервиса в пилюле (если сервер нашёл его в нашей базе иконок). */
function PillIcon({ node, label }: { node: SchemaNode; label: string }) {
  const file = node.icons?.[label];
  if (!file) return null;
  // eslint-disable-next-line @next/next/no-img-element -- статичный ассет из /public
  return <img className={styles.pillIco} src={`/service-icons/${file}`} alt="" aria-hidden="true" />;
}

export function Who({ name, mark }: { name: string; mark: string }) {
  return (
    <div className={styles.who}>
      <span className={styles.bdot}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={mark} alt="" aria-hidden="true" />
      </span>
      {name}
    </div>
  );
}

/** Логотип для КРУПНОГО значка шага: сервис пилюли, у ИИ-шага — выбранная модель. */
function dotIconFile(n: SchemaNode): string | undefined {
  if (n.pill && n.icons?.[n.pill]) return n.icons[n.pill];
  const model = n.sel ?? n.choose?.[0];
  return model ? n.icons?.[model] : undefined;
}

export function Schema({ dict, nodes }: { dict: GeneratorDict; nodes: SchemaNode[] }) {
  return (
    <div className={styles.schema}>
      {nodes.map((n, idx) => {
        const logo = dotIconFile(n);
        return (
        <div key={idx} className={`${styles.step} ${n.cls ? styles[n.cls] : ''}`}>
          <div className={styles.rail}>
            {/* Фирменный логотип — как есть, на нейтральной подложке; цветная заливка
                вида — ТОЛЬКО для рисованных иконок-заменителей (владелец 2026-07-14). */}
            <div className={`${styles.dot} ${logo ? styles.dotLogo : styles[`c-${n.i}`]}`}>
              {logo ? (
                // eslint-disable-next-line @next/next/no-img-element -- статичный ассет
                <img className={styles.dotImg} src={`/service-icons/${logo}`} alt="" aria-hidden="true" />
              ) : (
                <NodeIcon kind={n.i} />
              )}
            </div>
            <div className={styles.conn} />
          </div>
          <div className={styles.nbody}>
            <div className={styles.nt}>{n.t}</div>
            <div className={styles.ns}>{n.s}</div>
            <NodeLabel dict={dict} node={n} />
          </div>
        </div>
        );
      })}
    </div>
  );
}

// Резюме под схемой. «Нужен твой аккаунт» — ТОЛЬКО внешние сервисы (флаг acc с сервера:
// Telegram, DaData, Google-таблицы…). ИИ-модели в списке НЕ бывают НИКОГДА — поставщик
// моделей мы сами, аккаунты юзеру не нужны (владелец 2026-07-14).
export function Needs({ dict, nodes }: { dict: GeneratorDict; nodes: SchemaNode[] }) {
  const uniq = (a: string[]) => [...new Set(a.filter(Boolean))];
  // Telegram живёт в триггере без пилюли → ловим по названию шага.
  const tg = nodes.some((n) => n.pill === 'Telegram' || /telegram|телеграм/i.test(n.t));
  const accounts = uniq([
    ...(tg ? ['Telegram'] : []),
    ...nodes.filter((n) => n.acc && n.pill).map((n) => n.pillT ?? n.pill!),
  ]);
  const aiNodes = nodes.filter((n) => n.choose && n.choose.length > 0);
  // Модель шага: выбранная (sel) или первая из вариантов. Наши — по флагу ours (проставил подбор).
  const ourModels = uniq(aiNodes.filter((n) => n.ours === true).map((n) => n.sel || n.choose![0]!));
  const hasServer = nodes.some((n) => n.k === 'server');

  return (
    <div className={styles.needs}>
      <div className={styles.needsIntro}>{dict.needIntro}</div>
      {accounts.length > 0 && (
        <div className={styles.needsAccounts}>{dict.needAccounts.replace('%m', accounts.join(', '))}</div>
      )}
      {ourModels.length > 0 && (
        <div className={styles.needsOurs}>{dict.needOurs.replace('%m', ourModels.join(', '))}</div>
      )}
      {hasServer && <div className={styles.needsServer}>{dict.needServer}</div>}
    </div>
  );
}

function NodeLabel({ dict, node }: { dict: GeneratorDict; node: SchemaNode }) {
  // ИИ-шаг: выбор модели.
  if (node.choose) {
    return (
      <>
        <div className={styles.node}>
          <span className={styles.lbl}>{dict.labelChoose}</span>
          {node.choose.map((c) => (
            <span
              key={c}
              className={`${styles.pill} ${c === node.sel ? styles.sel : styles.alt}`}
            >
              <PillIcon node={node} label={c} />
              {c}
            </span>
          ))}
        </div>
        {node.picking ? (
          <div className={styles.picking}>{dict.picking}</div>
        ) : node.reason ? (
          <div className={styles.best}>
            {dict.best}: <b>{node.sel}</b>
            {node.reason ? ` — ${node.reason}` : ''}
          </div>
        ) : null}
        {node.note ? <div className={styles.note}>{node.note}</div> : null}
      </>
    );
  }

  // Реальный узел n8n / внешний сервис / триггер с именем → пилюля.
  if (node.pill) {
    const label =
      node.k === 'service' ? dict.labelService : node.k === 'trigger' ? dict.labelTrigger : dict.labelNode;
    return (
      <>
        <div className={styles.node}>
          <span className={styles.lbl}>{label}</span>
          <span className={styles.pill}>
            <PillIcon node={node} label={node.pill} />
            {node.pillT ?? node.pill}
          </span>
        </div>
        {node.note ? <div className={styles.note}>{node.note}</div> : null}
      </>
    );
  }

  // Без пилюли: бейдж «на нашем сервере» / «вы выбираете». Триггер — без бейджа, только подпись.
  const badge = node.k === 'server' ? dict.labelServer : node.k === 'human' ? dict.labelHuman : null;
  if (!badge && !node.note) return null;
  return (
    <>
      {badge ? (
        <div className={styles.node}>
          <span className={`${styles.pill} ${styles.badge}`}>{badge}</span>
        </div>
      ) : null}
      {node.note ? <div className={styles.note}>{node.note}</div> : null}
    </>
  );
}
